import { useEffect, useRef } from 'react';
import { LOGIN_METHOD, type RustyAuthSpec, type Session } from '@/api';

export interface UseOidcRefreshOnReturnParams {
  /** Current session — used to confirm the user is OIDC-backed before
   *  attempting the silent re-auth. */
  session: Session | undefined;
  /** Auth instance with `initiateOidcRefresh` available. */
  auth: RustyAuthSpec | undefined;
  /**
   * Query-param name that signals "we just returned from a handoff and the
   * page should refresh profile data from the IdP." Defaults to `refreshed`.
   */
  paramName?: string;
}

/**
 * Hook to fire on page mount: when the URL carries `?refreshed=1` AND the
 * current session is OIDC, triggers a silent OIDC re-auth that lands the user
 * back on the same page without the query param. Use on account-related
 * pages (profile, security log, etc.) so the refresh trigger Just Works
 * whether the user arrived via handoff banner click or manual sync button.
 *
 * For non-OIDC sessions OR when the param isn't present, the hook is a no-op.
 *
 * The trigger guard uses a ref so multiple mounts in the same tab don't
 * double-fire. The query param is stripped from the URL immediately so a back
 * navigation doesn't re-trigger the redirect dance.
 */
export function useOidcRefreshOnReturn({
  session,
  auth,
  paramName = 'refreshed',
}: UseOidcRefreshOnReturnParams) {
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    if (!session || session.method !== LOGIN_METHOD.OIDC) return;
    if (!auth) return;

    // The query param lives inside the hash for hash-routed apps, so peek at
    // both `location.search` and the post-`?` part of `location.hash`.
    const searchParams = new URLSearchParams(window.location.search);
    const hashQuery = window.location.hash.split('?')[1] ?? '';
    const hashParams = new URLSearchParams(hashQuery);
    const hasRefreshFlag = searchParams.has(paramName) || hashParams.has(paramName);
    if (!hasRefreshFlag) return;

    firedRef.current = true;

    // Strip the flag so a manual reload doesn't loop us. Same-origin URL is
    // safe to pass to `initiateOidcRefresh` (the backend validates origin).
    const stripped = stripRefreshFlag(window.location.href, paramName);
    auth.initiateOidcRefresh(stripped).then(
      (authUrl) => {
        window.location.href = authUrl;
      },
      (err) => {
        console.warn('[oidc] silent refresh-on-return failed:', err);
      }
    );
  }, [session, auth, paramName]);
}

function stripRefreshFlag(href: string, paramName: string): string {
  // We don't trust URL.canParse + manipulation across hash routers — do this
  // by hand to keep the hash route intact.
  const url = new URL(href);
  url.searchParams.delete(paramName);
  // Hash query string: `#/foo?refreshed=1` → `#/foo`.
  const [hashPath, hashQs] = url.hash.split('?');
  if (hashQs) {
    const hashParams = new URLSearchParams(hashQs);
    hashParams.delete(paramName);
    const rebuilt = hashParams.toString();
    url.hash = rebuilt ? `${hashPath}?${rebuilt}` : hashPath;
  }
  return url.toString();
}
